import { useEffect, useRef, useCallback } from 'react';

const STIFFNESS = 0.04;
const DAMPING = 0.82;
const MAX_TILT = 15;
const TILT_SENSITIVITY = 0.5;
const VELOCITY_THRESHOLD = 0.1;

const useSpringPhoto = ({ active, paragraphRef, onDeactivate }) => {
	const imageRef = useRef(null);
	const rafId = useRef(null);
	const isRunning = useRef(false);

	// Current animated position/velocity (never triggers re-render)
	const pos = useRef({ x: 0, y: 0 });
	const vel = useRef({ x: 0, y: 0 });
	const target = useRef({ x: 0, y: 0 });
	const visible = useRef(false);

	// Track mouse target without re-renders
	const handleMouseMove = useCallback((e) => {
		target.current.x = e.clientX;
		target.current.y = e.clientY;
	}, []);

	// Check if paragraph is in the viewport
	const isParagraphInView = useCallback(() => {
		if (!paragraphRef.current) return false;
		const rect = paragraphRef.current.getBoundingClientRect();
		return (
			rect.top < window.innerHeight &&
			rect.bottom > 0
		);
	}, [paragraphRef]);

	// Stable ref for onDeactivate to avoid re-creating scroll handler
	const onDeactivateRef = useRef(onDeactivate);
	useEffect(() => {
		onDeactivateRef.current = onDeactivate;
	}, [onDeactivate]);

	// Handle scroll — deactivate if paragraph leaves viewport
	const handleScroll = useCallback(() => {
		if (!visible.current) return;
		if (!isParagraphInView()) {
			visible.current = false;
			if (onDeactivateRef.current) {
				onDeactivateRef.current();
			}
		}
	}, [isParagraphInView]);

	// The rAF physics loop
	const tick = useCallback(() => {
		if (!imageRef.current) {
			isRunning.current = false;
			return;
		}

		const el = imageRef.current;

		// Spring force toward target
		const forceX = (target.current.x - pos.current.x) * STIFFNESS;
		const forceY = (target.current.y - pos.current.y) * STIFFNESS;

		vel.current.x = (vel.current.x + forceX) * DAMPING;
		vel.current.y = (vel.current.y + forceY) * DAMPING;

		pos.current.x += vel.current.x;
		pos.current.y += vel.current.y;

		// Tilt derived from velocity
		const tiltY = Math.max(-MAX_TILT, Math.min(MAX_TILT, vel.current.x * TILT_SENSITIVITY));
		const tiltX = Math.max(-MAX_TILT, Math.min(MAX_TILT, -vel.current.y * TILT_SENSITIVITY));

		// Get element dimensions for centering
		const w = el.offsetWidth;
		const h = el.offsetHeight;

		const tx = pos.current.x - w / 2;
		const ty = pos.current.y - h / 2;

		el.style.transform =
			`perspective(800px) translate(${tx}px, ${ty}px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;

		if (!visible.current) {
			const speed = Math.abs(vel.current.x) + Math.abs(vel.current.y);
			if (speed < VELOCITY_THRESHOLD) {
				isRunning.current = false;
				return;
			}
		}

		rafId.current = requestAnimationFrame(tick);
	}, []);

	// Start the rAF loop if not already running
	const startLoop = useCallback(() => {
		if (!isRunning.current) {
			isRunning.current = true;
			rafId.current = requestAnimationFrame(tick);
		}
	}, [tick]);

	// React to active prop changes
	useEffect(() => {
		if (active) {
			visible.current = true;
			startLoop();
		} else {
			visible.current = false;
			// Keep loop running briefly so transform can settle
			startLoop();
		}
	}, [active, startLoop]);

	// Attach / detach global listeners
	useEffect(() => {
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('scroll', handleScroll);
			if (rafId.current) {
				cancelAnimationFrame(rafId.current);
			}
		};
	}, [handleMouseMove, handleScroll]);

	return { imageRef };
};

export default useSpringPhoto;
